import type { GuidedState } from '../../pages/GuidedWalkthroughPage';

interface Props {
  state: GuidedState;
  onChange: (updates: Partial<GuidedState>) => void;
  onNext: () => void;
  onBack: () => void;
}

const NEEDS = [
  {
    key: 'needsSecurity' as const,
    icon: '🔒',
    label: 'Security',
    subtitle: 'Threat detection & incident response',
    desc: 'Detect attacks, investigate breaches, respond to alerts',
    note: 'Recommends Microsoft Sentinel + Analytics Logs',
  },
  {
    key: 'needsCompliance' as const,
    icon: '📋',
    label: 'Compliance',
    subtitle: 'Audit trail & regulatory evidence',
    desc: 'Prove compliance with regulations, provide audit evidence',
    note: 'Recommends Analytics Logs, extended retention',
  },
  {
    key: 'needsPerformance' as const,
    icon: '📊',
    label: 'Performance',
    subtitle: 'System health & troubleshooting',
    desc: 'Monitor server health, diagnose issues, track performance',
    note: 'Recommends Basic Logs for performance counters',
  },
  {
    key: 'needsApplicationLogs' as const,
    icon: '🌐',
    label: 'Application monitoring',
    subtitle: 'Track errors, usage & API performance',
    desc: 'Track application errors, user behaviour, API performance',
    note: 'Recommends Analytics Logs for app tables',
  },
];

export default function Step3MonitoringNeeds({ state, onChange, onNext, onBack }: Props) {
  const showSentinelToggle = state.needsSecurity || state.needsCompliance;

  const handleToggleNeed = (key: typeof NEEDS[number]['key']) => {
    const updated = { [key]: !state[key] } as Partial<GuidedState>;
    // If turning on Security, also suggest Sentinel
    if (key === 'needsSecurity' && !state.needsSecurity) {
      updated.sentinelEnabled = true;
    }
    onChange(updated);
  };

  return (
    <div>
      <h2 className="text-[22px] font-bold mb-1" style={{ color: 'var(--text-primary)' }}>What do you need to monitor?</h2>
      <p className="text-[14px] mb-8" style={{ color: 'var(--text-tertiary)' }}>
        This helps us recommend the right type of log storage for your needs. Select all that apply.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        {NEEDS.map((n) => {
          const selected = state[n.key];
          return (
            <button
              key={n.key}
              onClick={() => handleToggleNeed(n.key)}
              className={[
                'text-left p-4 rounded-lg border-2 transition-all',
                selected
                  ? 'border-[#0078d4] bg-[#0078d4]/15'
                  : 'hover:border-[#0078d4]/50',
              ].join(' ')}
            >
              <div className="flex items-start gap-3">
                <div className={[
                  'w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all',
                  selected ? 'bg-[#0078d4] border-[#0078d4]' : '',
                ].join(' ')}>
                  {selected && (
                    <span className="material-symbols-outlined text-[13px] text-white">check</span>
                  )}
                </div>
                <div>
                  <p className={`text-[14px] font-semibold mb-0.5 ${selected ? 'text-[#0078d4]' : ''}`} style={selected ? {} : { color: 'var(--text-secondary)' }}>
                    {n.icon} {n.label}
                    <span className="block text-[11px] font-normal mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{n.subtitle}</span>
                  </p>
                  <p className="text-[12px] mb-1" style={{ color: 'var(--text-tertiary)' }}>{n.desc}</p>
                  {selected && (
                    <p className="text-[11px] text-[#0078d4]">→ {n.note}</p>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {showSentinelToggle && (
        <div className="bg-[#0078d4]/10 border border-[#0078d4]/40 rounded-lg p-4 mb-6">
          <p className="text-[13px] mb-3" style={{ color: 'var(--text-secondary)' }}>
            Based on your selections, we recommend <strong>Microsoft Sentinel</strong> for automated
            threat detection and compliance reporting.
          </p>
          <label className="flex items-center gap-3 cursor-pointer">
            <button
              role="switch"
              aria-checked={state.sentinelEnabled}
              onClick={() => onChange({ sentinelEnabled: !state.sentinelEnabled })}
              style={state.sentinelEnabled
                ? { backgroundColor: '#0078d4' }
                : { backgroundColor: 'var(--outline-variant)', border: '1px solid var(--outline)' }}
              className="relative w-10 h-5 rounded-full transition-all"
            >
              <span className={[
                'absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all',
                state.sentinelEnabled ? 'left-5' : 'left-0.5',
              ].join(' ')} />
            </button>
            <span className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
              Include Microsoft Sentinel costs
            </span>
            <span className={`text-[11px] font-semibold ml-auto ${state.sentinelEnabled ? 'text-[#0078d4]' : ''}`} style={state.sentinelEnabled ? {} : { color: 'var(--text-tertiary)' }}>
              {state.sentinelEnabled ? 'ON' : 'OFF'}
            </span>
          </label>
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="px-5 py-3 rounded-lg text-[14px] transition-all flex items-center gap-2"
          style={{ border: '1px solid var(--outline-variant)', color: 'var(--text-tertiary)' }}
        >
          <span className="material-symbols-outlined text-[18px]">arrow_back</span>
          Back
        </button>
        <button
          onClick={onNext}
          className="flex-1 py-3 rounded-lg font-semibold text-[14px] bg-[#0078d4] hover:bg-[#106ebe] text-white transition-all flex items-center justify-center gap-2"
        >
          Next: Compliance & Retention
          <span className="material-symbols-outlined text-[18px]">arrow_forward</span>
        </button>
      </div>
    </div>
  );
}
